import { useState, useSyncExternalStore } from 'react';
import { appCopy } from '@zibanu/shared';
import { Banner, Button } from '@zibanu/ui';
import { useDeps } from './deps';

/** The browser's install offer (Chrome on Android); not in TypeScript's DOM types. */
interface InstallOffer extends Event {
  prompt(): Promise<void>;
}

export type InstallWay = 'button' | 'ios-hint' | null;

export interface InstallStore {
  /** How this browser installs the app, if it can and hasn't. */
  way(): InstallWay;
  install(): Promise<void>;
  subscribe(listener: () => void): () => void;
}

/**
 * Installing the app (spec 3A §6.5). Android offers it: the offer is caught
 * and Install shown. iPhone never offers: Safari outside the installed app
 * gets the Share hint. Made once in main.tsx, before React, so an early
 * offer isn't missed.
 */
export function createInstallStore(win: Window, ua: string = win.navigator.userAgent): InstallStore {
  let offer: InstallOffer | null = null;
  const listeners = new Set<() => void>();
  const changed = () => listeners.forEach((l) => l());
  const installed = () =>
    win.matchMedia?.('(display-mode: standalone)').matches === true || (win.navigator as { standalone?: boolean }).standalone === true;
  win.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    offer = e as InstallOffer;
    changed();
  });
  win.addEventListener('appinstalled', () => {
    offer = null;
    changed();
  });
  return {
    way: () => (installed() ? null : offer ? 'button' : /iPhone|iPad|iPod/.test(ua) ? 'ios-hint' : null),
    async install() {
      const o = offer;
      if (!o) return;
      offer = null;
      changed();
      await o.prompt();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const DISMISSED = 'zibanu:install-dismissed';

/** A per-phone convenience: storage may be missing, and then the hint just shows again. */
function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED) === '1';
  } catch {
    return false;
  }
}

function useInstallWay(): InstallWay {
  const { install } = useDeps().platform;
  return useSyncExternalStore(install.subscribe, install.way);
}

/** Home's install banner: Install on Android, the Share hint on iPhone, until dismissed. */
export function InstallBanner() {
  const { install } = useDeps().platform;
  const way = useInstallWay();
  const [dismissed, setDismissed] = useState(wasDismissed);
  if (!way || dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED, '1');
    } catch {
      // Not kept: the hint comes back next time, which is harmless.
    }
  };
  return way === 'button' ? (
    <Banner tone="info" title={appCopy.installLead()} action={appCopy.install()} onAction={() => void install.install()} onDismiss={dismiss} />
  ) : (
    <Banner tone="info" title={appCopy.installLead()} onDismiss={dismiss}>
      {appCopy.iosInstallHint()}
    </Banner>
  );
}

/** More keeps the way to install, dismissed or not. */
export function InstallInMore() {
  const { install } = useDeps().platform;
  const way = useInstallWay();
  if (!way) return null;
  return (
    <>
      <p className="zw-muted">{way === 'button' ? appCopy.installLead() : appCopy.iosInstallHint()}</p>
      {way === 'button' && (
        <Button variant="secondary" icon="download" onClick={() => void install.install()}>
          {appCopy.install()}
        </Button>
      )}
    </>
  );
}
